import { MouseEventHandler, ReactElement, useContext, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Chip, ChipProps, Fade, Typography } from '@mui/material';
import { generateHexFromString } from '@/_utils/colorUtils';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { ConfigContext } from '@/contextAPI/configContext';
import { selectSelectedHierarchy, selectVersions } from '@/redux/selectors/orderPlacementSelectors';
import type { TitleEntity } from '@/redux/slices/slice_types';
import type { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { selectTitleDataById } from '@/redux/selectors/titleSelectors';
import { selectTitlesById } from '@/redux/selectors/titleV2Selectors';
import { addVersionKeys, setVersionSelections } from '@/redux/slices/orderPlacementSlice';
import ConditionalTooltip from './ConditionalTooltip/ConditionalTooltip';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import focusConfig from '@/components/Focus/configs';

type TitleVersionChipProps = ChipProps & {
  version: TitleEntity | TitleEntityV2;
  size?: ChipProps['size'];
  clickable?: boolean;
  withTooltip?: boolean;
  tooltip?: string | null;
  tooltipSource?: 'global-channel-ids' | 'name';
};

const TitleVersionChip = ({
  version = {} as TitleEntity | TitleEntityV2,
  size = 'medium',
  clickable = true,
  withTooltip = false,
  tooltip,
  tooltipSource,
  ...props
}: TitleVersionChipProps) => {
  const isFocus = window.location.pathname.includes('focus');
  const dispatch = useAppDispatch();
  const versionColors = useContext(ConfigContext)?.versionColors || [];
  const focusVersionColor = isFocus && focusConfig.versionColors ? focusConfig.versionColors : undefined;
  const selectedVersions = useAppSelector(selectVersions);
  const isTitleApiV2: boolean | undefined = useContext(ConfigContext)?.titleConfig?.v2; // TODO(TMT-6046): remove it when all clients migrated to the V2 title api
  const titlesById = isTitleApiV2 ? useAppSelector(selectTitlesById) : useAppSelector(selectTitleDataById);
  const versionChipTooltip = useContext(ConfigContext)?.orderPlacementConfig?.versionChipTooltip;
  const selectedHierarchy = useAppSelector(selectSelectedHierarchy);
  const [showOverflowTooltip, setShowOverflowTooltip] = useState(false);
  const versionType = (version?.version_type || version.meta?.friendly_name || version.name)?.toLowerCase() || '';
  const color =
    focusVersionColor && focusVersionColor[versionType]
      ? focusVersionColor[versionType]
      : versionColors[versionType] || generateHexFromString(versionType) || '#000';
  const versionTypeRef = useRef(null);
  const isSelected = (selectedVersions as TitleEntity[]).find((v) => v.id === version.id);

  const handleClick: MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();

    dispatch(setVersionSelections(version));
    if (version.lineage.length > 1) {
      const titles = version.lineage.map((id) => titlesById[id]);
      dispatch(addVersionKeys(titles));
    }
  };

  useEffect(() => {
    if (versionTypeRef.current) {
      const { offsetWidth, scrollWidth } = versionTypeRef.current;
      if (offsetWidth < scrollWidth) {
        setShowOverflowTooltip(true);
      }
    }
    return () => {
      setShowOverflowTooltip(false);
    };
  }, []);

  const isEmbargoed = version.is_embargoed;
  const renderTooltip = () => {
    let lines: string[] = [];
    if (tooltip) {
      lines = [tooltip];
      if (isEmbargoed) lines.push('⚠️ Embargoed');
    } else if (withTooltip) {
      const base = versionChipTooltip ? versionChipTooltip(version, tooltipSource, selectedHierarchy) : version.external_id || version.id;
      lines = [base];
      if (isEmbargoed) lines.push('⚠️ Embargoed');
    } else {
      lines = [versionType.toUpperCase()];
    }
    return (
      <div style={{ textAlign: 'center', whiteSpace: 'pre-line' }}>
        {lines.map((line, idx) => (
          <div key={idx}>{line}</div>
        ))}
      </div>
    );
  };

  return (
    <Fade in>
      <div>
        <ConditionalTooltip show={withTooltip || showOverflowTooltip} title={renderTooltip()} arrow>
          <ChipInnerWrapper>
            <StyledChip
              key={version.id}
              label={
                <Typography noWrap sx={size == 'small' ? { fontSize: '.9rem' } : { maxWidth: 150 }} ref={versionTypeRef}>
                  {versionType.toUpperCase()}
                </Typography>
              }
              clickable={clickable}
              size={size}
              variant="outlined"
              color="primary"
              disabled={version.disabled}
              icon={
                clickable
                  ? ((
                      <PolarisIcon icon={isSelected ? 'check' : 'add'} height={15} style={{ minWidth: 15, marginLeft: size == 'small' ? 0 : 8 }} />
                    ) as ReactElement<any, any>)
                  : undefined
              }
              onClick={clickable ? handleClick : undefined}
              sx={{ minWidth: 80, borderRadius: 5, px: size === 'small' ? 0.5 : 0 }}
              selected={+!!isSelected}
              bg={color}
              {...props}
            />
          </ChipInnerWrapper>
        </ConditionalTooltip>
      </div>
    </Fade>
  );
};

export default TitleVersionChip;

const ChipInnerWrapper = styled.div`
  display: inline-block;
`;

const StyledChip = styled(Chip)<{ selected: number; bg: string }>`
  margin: ${({ theme }) => theme.spacing(0.25, 0, 0.25, 1)};
  border: 1px solid;
  color: ${({ selected, theme, bg }) => (selected ? theme.palette.background.default : bg)};
  border-color: ${({ bg }) => bg};

  &,
  &.MuiChip-root:not(.MuiChip-clickable).MuiChip-outlined {
    ${({ selected, theme, bg: selectedColor }) =>
      theme.customOverrides?.TitleVersionChip
        ? `
    background-color: ${selected ? selectedColor : theme.customOverrides?.TitleVersionChip?.backgroundColor};
    color: ${selected ? theme.customOverrides?.TitleVersionChip?.color : selectedColor};
    `
        : `
    background-color: ${selected ? selectedColor : 'transparent'};
    `}
  }

  &.Mui-disabled {
    opacity: ${({ theme }) => `${theme.palette.mode == 'light' ? 0.2 : 0.4} !important`};
  }

  &.MuiChip-clickable:hover {
    ${({ selected, theme, bg: selectedColor }) =>
      theme.customOverrides?.TitleVersionChip
        ? `
    background-color: ${!selected ? selectedColor : theme.customOverrides?.TitleVersionChip?.backgroundColor};
    color: ${!selected ? theme.customOverrides?.TitleVersionChip?.color : selectedColor};
    `
        : `
    background-color: ${selected ? 'none' : selectedColor};
    color: ${selected ? selectedColor : theme.palette.background.default};
    `}
  }
`;
